import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import ChartRenderer from './ChartRenderer';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import { useTheme } from '../context/ThemeContext';
import { CheckCircle, XCircle, MessageSquare, Lightbulb } from 'lucide-react';
import 'highlight.js/styles/github.css';
import 'katex/dist/katex.min.css';

const EditorContainer = styled.div`
  display: flex;
  height: 100%;
  background: ${props => props.theme.mode === 'dark' ? '#1e1e1e' : '#ffffff'};
  border: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
  border-radius: 8px;
  overflow: hidden;
  font-family: 'Noto Sans KR', 'Malgun Gothic', 'Apple SD Gothic Neo', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-family: 'Noto Sans KR', 'Malgun Gothic', 'Apple SD Gothic Neo', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-family: 'Noto Sans KR', 'Malgun Gothic', 'Apple SD Gothic Neo', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  padding-bottom: 36px; /* 상태바 공간 확보 */
`;

const TextArea = styled.textarea`
  flex: 1;
  padding: 40px 60px;
  border: none;
  outline: none;
  resize: none;
  font-size: 16px;
  line-height: 1.6;
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  background: ${props => props.theme.mode === 'dark' ? '#1e1e1e' : '#ffffff'};
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  
  &::placeholder {
    color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#8b949e'};
  }
  
  /* 워드 스타일 스크롤바 */
  &::-webkit-scrollbar {
    width: 12px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f6f8fa;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d1d9e0;
    border-radius: 6px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8b2ba;
  }
`;

const PreviewArea = styled.div`
  flex: 1;
  padding: 40px 60px;
  background: ${props => props.theme.mode === 'dark' ? '#1e1e1e' : '#ffffff'};
  border-left: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
  overflow-y: auto;
  font-family: 'Noto Sans KR', 'Malgun Gothic', 'Apple SD Gothic Neo', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  width: 100%;
  box-sizing: border-box;
  word-break: break-word;
  overflow-wrap: anywhere;
  /* 워드 스타일 스크롤바 */
  &::-webkit-scrollbar {
    width: 12px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f6f8fa;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #d1d9e0;
    border-radius: 6px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8b2ba;
  }
  
  /* 페이지 분할 힌트 (PDF 내보내기용) */
  p, h1, h2, h3, h4, h5, h6, ul, ol, pre, blockquote, table {
    page-break-inside: avoid;
    break-inside: avoid;
  }
  pre, table {
    page-break-inside: auto;
    break-inside: auto;
  }
  .page-break {
    page-break-before: always;
    break-before: page;
  }
  
  /* 워드 스타일 마크다운 렌더링 */
  h1 {
    font-size: 32px;
    font-weight: 700;
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
    margin: 24px 0 16px 0;
    line-height: 1.25;
    border-bottom: 2px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
    padding-bottom: 8px;
  }
  
  h2 {
    font-size: 24px;
    font-weight: 600;
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
    margin: 20px 0 12px 0;
    line-height: 1.25;
    border-bottom: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
    padding-bottom: 6px;
  }
  
  h3 {
    font-size: 20px;
    font-weight: 600;
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
    margin: 16px 0 8px 0;
    line-height: 1.25;
  }
  
  h4 {
    font-size: 18px;
    font-weight: 600;
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
    margin: 14px 0 6px 0;
    line-height: 1.25;
  }
  
  h5 {
    font-size: 16px;
    font-weight: 600;
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
    margin: 12px 0 4px 0;
    line-height: 1.25;
  }
  
  h6 {
    font-size: 14px;
    font-weight: 600;
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
    margin: 10px 0 4px 0;
    line-height: 1.25;
  }
  
  p {
    font-size: 16px;
    line-height: 1.6;
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
    margin: 8px 0;
  }
  
  strong, b {
    font-weight: 700;
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  }
  
  em, i {
    font-style: italic;
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  }
  
  ul, ol {
    margin: 8px 0;
    padding-left: 24px;
  }
  
  li {
    font-size: 16px;
    line-height: 1.6;
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
    margin: 4px 0;
  }
  
  blockquote {
    border-left: 4px solid #0969da;
    padding-left: 16px;
    margin: 16px 0;
    color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#586069'};
    font-style: italic;
  }
  
  code {
    background: ${props => props.theme.mode === 'dark' ? '#21262d' : '#f6f8fa'};
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 14px;
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#e36209'};
  }
  
  pre {
    background: ${props => props.theme.mode === 'dark' ? '#21262d' : '#f6f8fa'};
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 16px 0;
  }
  
  pre code {
    background: none;
    padding: 0;
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  }
  
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 16px 0;
    font-family: 'Noto Sans KR', 'Malgun Gothic', 'Apple SD Gothic Neo', 'Segoe UI', sans-serif;
  }
  
  th, td {
    border: 1px solid ${props => props.theme.mode === 'dark' ? '#30363d' : '#d1d9e0'};
    padding: 10px 14px;
    text-align: left;
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
    vertical-align: top;
    font-size: 14px;
  }
  
  th {
    background: ${props => props.theme.mode === 'dark' ? '#1f2937' : '#f0f3f7'};
    font-weight: 700;
    color: ${props => props.theme.mode === 'dark' ? '#e5e7eb' : '#1f2937'};
  }
  
  hr {
    border: none;
    border-top: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
    margin: 24px 0;
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: #0969da;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  z-index: 10;
  
  &:hover {
    background: #0550ae;
  }
`;

// 하단 상태 표시줄
const StatusBar = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  padding: 0 16px;
  border-top: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
  background: ${props => props.theme.mode === 'dark' ? 'rgba(30,30,30,0.9)' : 'rgba(255,255,255,0.9)'};
  font-size: 12px;
  color: ${props => props.theme.mode === 'dark' ? '#cbd5e1' : '#374151'};
  backdrop-filter: blur(6px);
  z-index: 5;
`;

// 플로팅 가로 원형 통계 표시
const FloatingStats = styled.div`
  position: absolute;
  right: 16px;
  bottom: 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 9999px;
  background: ${props => props.theme.mode === 'dark' ? 'rgba(32,32,36,0.75)' : 'rgba(255,255,255,0.8)'};
  border: 1px solid ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  box-shadow: 0 6px 24px rgba(0,0,0,0.18);
  backdrop-filter: blur(8px);
  color: ${props => props.theme.mode === 'dark' ? '#e5e7eb' : '#111827'};
  font-size: 12px;
`;

const StatChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 9999px;
  background: ${props => props.theme.mode === 'dark' ? 'rgba(46, 52, 64, 0.6)' : 'rgba(243, 244, 246, 0.8)'};
  border: 1px solid ${props => props.theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'};
`;

// AI 미리보기 관련 스타일
const PreviewOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const PreviewContainer = styled.div`
  background: ${props => props.theme.mode === 'dark' ? '#1e1e1e' : '#ffffff'};
  border: 2px solid #0969da;
  border-radius: 12px;
  padding: 20px;
  max-width: 80%;
  max-height: 80%;
  overflow-y: auto;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  position: relative;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
`;

const PreviewTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  margin: 0;
`;

const PreviewActions = styled.div`
  display: flex;
  gap: 8px;
`;

const PreviewButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &.apply {
    background: #28a745;
    color: white;
    &:hover {
      background: #218838;
    }
  }
  
  &.reject {
    background: #dc3545;
    color: white;
    &:hover {
      background: #c82333;
    }
  }
`;

const PreviewContent = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  white-space: pre-wrap;
  word-wrap: break-word;
`;

// 선택된 부분 미리보기 스타일
const SelectionPreviewOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  pointer-events: auto;
`;

const SelectionPreviewContainer = styled.div`
  background: ${props => props.theme.mode === 'dark' ? 'rgba(45, 51, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)'};
  border: 2px solid ${props => props.theme.mode === 'dark' ? '#0969da' : '#0969da'};
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(9, 105, 218, 0.3);
  backdrop-filter: blur(8px);
  max-width: 80%;
  max-height: 80%;
  overflow: hidden;
  position: relative;
`;

const SelectionPreviewHeader = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid ${props => props.theme.mode === 'dark' ? '#484f58' : '#e1e4e8'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${props => props.theme.mode === 'dark' ? 'rgba(9, 105, 218, 0.1)' : 'rgba(9, 105, 218, 0.05)'};
`;

const SelectionPreviewTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SelectionPreviewActions = styled.div`
  display: flex;
  gap: 6px;
`;

const SelectionPreviewButton = styled.button`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
  
  &.apply {
    background: #28a745;
    color: white;
    
    &:hover {
      background: #218838;
      transform: translateY(-1px);
    }
  }
  
  &.reject {
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
      transform: translateY(-1px);
    }
  }
`;

const SelectionPreviewContent = styled.div`
  padding: 12px;
  max-height: 200px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.5;
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  white-space: pre-wrap;
  word-wrap: break-word;
`;

// 텍스트 선택 관련 스타일
const SelectionTooltip = styled.div`
  position: absolute;
  background: ${props => props.theme.mode === 'dark' ? '#2d333b' : '#ffffff'};
  border: 1px solid ${props => props.theme.mode === 'dark' ? '#484f58' : '#d1d9e0'};
  border-radius: 6px;
  padding: 6px 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${props => props.theme.mode === 'dark' ? '#484f58' : '#f6f8fa'};
    border-color: ${props => props.theme.mode === 'dark' ? '#6e7681' : '#0969da'};
  }
`;

const AddChatButton = styled.button`
  background: #0969da;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #0550ae;
  }
`;

const WordEditor = ({ 
  content, 
  onChange, 
  onSave, 
  onManualSave,
  previewContent, 
  previewMode, 
  onApplyPreview, 
  onRejectPreview,
  onStartChat
}) => {
  const DEBUG = false; // 로그 제어 플래그
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [selectionRange, setSelectionRange] = useState({ start: 0, end: 0 });
  const [selectionLineInfo, setSelectionLineInfo] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const textareaRef = useRef(null);
  const containerRef = useRef(null);
  const { theme } = useTheme();
  const [stats, setStats] = useState({ chars: 0, words: 0, lines: 0, sel: 0 });

  const computeStats = (text, selectionLen = 0) => {
    const chars = text.length;
    const words = (text.trim().match(/\S+/g) || []).length;
    const lines = text.split('\n').length;
    setStats({ chars, words, lines, sel: selectionLen });
  };

  // 수식 정규화: \( ... \) → $...$, \[ ... \] → $$...$$ (간단 전처리)
  const normalizeMathDelimiters = (text) => {
    if (!text) return text;
    return text
      .replace(/\\\[/g, '$$')
      .replace(/\\\]/g, '$$')
      .replace(/\\\(/g, '$')
      .replace(/\\\)/g, '$');
  };

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const handleKeyDown = (e) => {
    // Ctrl+S → 수동 저장(최근파일 스냅샷), 다운로드 다이얼로그 열지 않음
    if (e.ctrlKey && (e.key === 's' || e.key === 'S')) {
      e.preventDefault();
      if (onManualSave) onManualSave();
      return;
    }
  };

  const togglePreview = () => {
    setIsPreviewMode(!isPreviewMode);
  };

  const handleTextSelection = (e) => {
    const textarea = e.target;
    const selectionStart = textarea.selectionStart;
    const selectionEnd = textarea.selectionEnd;
    const selectedText = textarea.value.substring(selectionStart, selectionEnd);
    // 상태바 통계 업데이트
    computeStats(textarea.value, selectedText.length);
    
    if (DEBUG) console.log('텍스트 선택 이벤트:', {
      selectionStart,
      selectionEnd,
      selectedText: selectedText.substring(0, 50) + '...',
      selectedTextLength: selectedText.length
    });
    
    if (selectedText.trim().length > 0) {
      setSelectedText(selectedText);
      setSelectionRange({ start: selectionStart, end: selectionEnd });
      
      // 선택된 라인 정보 계산
      const textBeforeStart = textarea.value.substring(0, selectionStart);
      const textBeforeEnd = textarea.value.substring(0, selectionEnd);
      const startLine = textBeforeStart.split('\n').length - 1;
      const endLine = textBeforeEnd.split('\n').length - 1;
      
      setSelectionLineInfo({
        startLine,
        endLine,
        startChar: textBeforeStart.split('\n')[startLine].length,
        endChar: textBeforeEnd.split('\n')[endLine].length
      });
      
      // 툴팁 위치 계산 - 선택 영역 끝 부분에 표시
      const rect = textarea.getBoundingClientRect();
      const containerRect = containerRef.current?.getBoundingClientRect();
      
      if (containerRect) {
        // 선택된 텍스트의 끝 부분 계산
        const textBeforeEnd = textarea.value.substring(0, selectionEnd);
        const lines = textBeforeEnd.split('\n');
        const lastLine = lines[lines.length - 1];
        
        // 대략적인 위치 계산
        const charWidth = 9; // 문자 너비 (조금 더 정확하게)
        const lineHeight = 24; // 줄 높이
        
        // X 위치: 마지막 줄의 끝 부분 (컨테이너 경계 고려)
        const rawX = lastLine.length * charWidth + 20;
        const x = Math.max(20, Math.min(rawX, containerRect.width - 140));
        
        // Y 위치: 줄 번호에 따른 위치 (컨테이너 높이 고려)
        const lineNumber = lines.length - 1;
        const rawY = lineNumber * lineHeight + 30;
        const y = Math.max(20, Math.min(rawY, containerRect.height - 60));
        
        setTooltipPosition({
          x: x,
          y: y
        });
        setShowTooltip(true);
        if (DEBUG) console.log('툴팁 표시 (개선된 위치):', { 
          x: x, 
          y: y,
          rawX,
          rawY,
          lineNumber,
          lastLineLength: lastLine.length,
          containerWidth: containerRect.width,
          containerHeight: containerRect.height
        });
      }
    } else {
      setShowTooltip(false);
      // 선택이 없을 때도 통계 갱신
      computeStats(textarea.value, 0);
    }
  };

  const handleStartChat = () => {
    if (DEBUG) console.log('Add Chat 클릭:', {
      selectedText: selectedText.substring(0, 50) + '...',
      selectionRange,
      selectionLineInfo,
      onStartChat: typeof onStartChat
    });
    
    if (selectedText.trim().length > 0 && onStartChat) {
      onStartChat(selectedText, selectionRange);
      setShowTooltip(false);
    } else {
      if (DEBUG) console.log('Add Chat 실행 실패:', {
        hasSelectedText: selectedText.trim().length > 0,
        hasOnStartChat: !!onStartChat
      });
    }
  };

  const handleClickOutside = (e) => {
    if (containerRef.current && !containerRef.current.contains(e.target)) {
      setShowTooltip(false);
    }
  };

  // 클릭 외부 감지
  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  // 미리보기가 없어질 때 선택 정보 초기화
  useEffect(() => {
    if (!previewContent) {
      setSelectionLineInfo(null);
    }
  }, [previewContent]);

  return (
    <div ref={containerRef} style={{ position: 'relative', height: '100%' }}>
      <ToggleButton onClick={togglePreview}>
        {isPreviewMode ? '편집 모드' : '미리보기'}
      </ToggleButton>
      
      <EditorContainer>
        {!isPreviewMode && (
          <TextArea
            ref={textareaRef}
            value={content}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onSelect={handleTextSelection}
            placeholder="AI 글 작성하기를 눌러 글을 작성해주세요. 직접 작성하셔도 됩니다. 또한, 반드시 주의 사항과 도움말을 확인하세요. (Ctrl+S로 저장)"
            style={{ width: isPreviewMode ? '0' : '100%' }}
          />
        )}
        
        {isPreviewMode && (
          <PreviewArea id="export-preview-area">
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkMath]}
              rehypePlugins={[rehypeRaw, rehypeHighlight, rehypeKatex]}
              components={{
                code({node, inline, className, children, ...props}) {
                  const text = String(children || '').trim();
                  if (!inline && text.startsWith('chart')) {
                    // fenced block starting with 'chart' language → next line JSON
                    try {
                      const jsonStart = text.indexOf('{');
                      const json = jsonStart >= 0 ? text.slice(jsonStart) : '{}';
                      const spec = JSON.parse(json);
                      return <ChartRenderer spec={spec} />;
                    } catch (e) {
                      return <pre><code {...props}>{text}</code></pre>;
                    }
                  }
                  return <code className={className} {...props}>{children}</code>;
                }
              }}
            >
              {normalizeMathDelimiters(content) || '*문서가 비어있습니다.*'}
            </ReactMarkdown>
          </PreviewArea>
        )}
      </EditorContainer>
      
      {/* 텍스트 선택 툴팁 */}
      {showTooltip && !isPreviewMode && (
        <SelectionTooltip
          theme={theme}
          style={{
            left: tooltipPosition.x,
            top: tooltipPosition.y
          }}
          onClick={handleStartChat}
        >
          <MessageSquare size={16} />
          <span>Add Chat</span>
        </SelectionTooltip>
      )}
      
      {/* AI 미리보기 오버레이 */}
      {previewContent && previewMode && (
        window.__aiLastPatches && previewMode === 'edit-patch' && Array.isArray(window.__aiLastPatches) ? (
          <PreviewOverlay>
            <PreviewContainer theme={theme}>
              <PreviewHeader theme={theme}>
                <PreviewTitle theme={theme}>AI가 추천하는 부분별 수정 미리보기</PreviewTitle>
                <PreviewActions>
                  <PreviewButton className="apply" onClick={() => { window.__aiLastPatches = undefined; onApplyPreview(); }}>
                    <CheckCircle size={16} />
                    전체 적용
                  </PreviewButton>
                  <PreviewButton className="reject" onClick={() => { window.__aiLastPatches = undefined; onRejectPreview(); }}>
                    <XCircle size={16} />
                    취소
                  </PreviewButton>
                </PreviewActions>
              </PreviewHeader>
              <div style={{maxHeight: '52vh',overflowY:'auto',display:'flex',flexDirection:'column',gap:24}}>
                {window.__aiLastPatches.map((patch, idx) => (
                  <div key={idx} style={{background:(theme.mode==='dark'?'#21262d':'#f6f8fa'), borderLeft:'5px solid #0969da', borderRadius:7,padding:'18px 22px',marginBottom:5,boxShadow:'0 3px 10px 0 rgba(9,105,218,0.05)'}}>
                    <div style={{fontWeight:700,fontSize:14,color:'#2186e7',marginBottom:6}}>{patch.type==='replace'? '부분 교체':'내용 추가'}</div>
                    <div style={{fontSize:13,color:'#777',marginBottom:10}}>
                      {patch.type==='replace' && <>
                        <b>원문:</b> <span style={{color:'#b64b3b'}}>{patch.target}</span>
                      </>}
                      {patch.type==='insert_after' && <>
                        <b>이후 추가:</b> <span style={{color:'#4b5eb6'}}>{patch.target}</span>
                      </>}
                      {patch.type==='insert_before' && <>
                        <b>이전 추가:</b> <span style={{color:'#4b5eb6'}}>{patch.target}</span>
                      </>}
                    </div>
                    <div style={{fontSize:15,color:(theme.mode==='dark')?'#f8f8ff':'#24292e',background:'#fff8f1',padding:'13px 16px',borderRadius:6,minHeight:24}}>
                      {patch.type==='replace'
                        ? <><b>→ </b>{patch.replacement ?? ''}</>
                        : (patch.content ?? '')}
                    </div>
                  </div>
                ))}
              </div>
            </PreviewContainer>
          </PreviewOverlay>
        ) : (
          <PreviewOverlay>
            <PreviewContainer theme={theme}>
              <PreviewHeader theme={theme}>
                <PreviewTitle theme={theme}>
                  {previewMode === 'edit' ? '부분 수정 미리보기'
                    : previewMode === 'write' ? 'AI 작성 미리보기'
                    : 'AI 미리보기'}
                </PreviewTitle>
                <PreviewActions>
                  <PreviewButton className="apply" onClick={onApplyPreview}>
                    <CheckCircle size={16} />
                    적용
                  </PreviewButton>
                  <PreviewButton className="reject" onClick={onRejectPreview}>
                    <XCircle size={16} />
                    취소
                  </PreviewButton>
                </PreviewActions>
              </PreviewHeader>
              <PreviewContent theme={theme}>
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkMath]}
                  rehypePlugins={[rehypeRaw, rehypeHighlight, rehypeKatex]}
                >
                  {normalizeMathDelimiters(previewContent)}
                </ReactMarkdown>
              </PreviewContent>
            </PreviewContainer>
          </PreviewOverlay>
        )
      )}

      {/* 플로팅 가로 원형 통계 */}
      {!isPreviewMode && (
        <FloatingStats theme={theme}>
          <StatChip theme={theme}>문자 {stats.chars.toLocaleString()}</StatChip>
          <StatChip theme={theme}>단어 {stats.words.toLocaleString()}</StatChip>
          <StatChip theme={theme}>줄 {stats.lines.toLocaleString()}</StatChip>
          {stats.sel > 0 && <StatChip theme={theme}>선택 {stats.sel.toLocaleString()}</StatChip>}
        </FloatingStats>
      )}
    </div>
  );
};

export default WordEditor;